import { $http } from '@/common/api/http.service';
import { useAuthStore } from '@/stores/auth';
import { ref } from 'vue';

/**
 * Sentinel outcomeToolsId written by the Classic import when the migrating practice
 * used OutcomeTools: the patient may already exist in OT and has to be resolved
 * before the chart can say whether they are linked. Treating it as a linked value
 * would render survey history for a client id that does not exist.
 */
export const OT_UNKNOWN_LINK = 'unknown';

/**
 * API composable for the OutcomeTools V2 (SOR partner) integration setup.
 * Distinct from useOutcomeToolsApi, which talks to the V1 endpoints.
 */
export function useOutcomeToolsSorApi() {
    const loading = ref(false);
    const error = ref(null);
    const authStore = useAuthStore();

    const baseUrl = () => `/api/account/${authStore.account.id}/outcome-tools-sor`;

    const getStatus = async () => {
        const response = await $http.get(`${baseUrl()}/status`, { errorHandle: false });
        return response.data;
    };

    const enable = async (payload) => {
        loading.value = true;
        error.value = null;
        try {
            const response = await $http.post(`${baseUrl()}/enable`, payload, { errorHandle: false });
            return response.data;
        } catch (err) {
            error.value = err;
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const listUsers = async () => {
        const response = await $http.get(`${baseUrl()}/users`, { errorHandle: false });
        return response.data?.users ?? [];
    };

    // level is optional — omitted, the server applies its default (4);
    // 5 = OutcomeTools administrator.
    const addUser = async (practitionerId, level) => {
        loading.value = true;
        error.value = null;
        try {
            const payload = { practitionerId };
            if (level !== undefined) {
                payload.level = level;
            }
            const response = await $http.post(`${baseUrl()}/users`, payload, { errorHandle: false });
            return response.data;
        } catch (err) {
            error.value = err;
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const linkUser = async (otUserId, practitionerId) => {
        loading.value = true;
        error.value = null;
        try {
            const response = await $http.post(`${baseUrl()}/users/${otUserId}/link`, { practitionerId }, { errorHandle: false });
            return response.data;
        } catch (err) {
            error.value = err;
            throw err;
        } finally {
            loading.value = false;
        }
    };

    // Full (re)build of OT programs from Fresh. Run before syncFacilities —
    // facility payloads reference the OT program codes this creates.
    // The structure a practice already has in OutcomeTools plus the Fresh programs
    // and locations it can map onto. Returns { otPrograms, otFacilities,
    // freshPrograms, freshLocations }; empty OT lists mean there is nothing to map.
    // Pass freshOnly when all you need is which BestNotes items are missing from
    // OutcomeTools — it skips the two upstream list calls.
    const getStructureMapping = async ({ freshOnly = false } = {}) => {
        const url = freshOnly ? `${baseUrl()}/structure-mapping?scope=fresh` : `${baseUrl()}/structure-mapping`;
        const response = await $http.get(url, { errorHandle: false });
        return response.data;
    };

    // Apply the mapping: each entry either re-points an OT item at a Fresh resource
    // (sorId) or archives it. Returns { mapped, archived, failed, errors }.
    const saveStructureMapping = async (payload) => {
        const response = await $http.post(`${baseUrl()}/structure-mapping`, payload, { errorHandle: false });
        return response.data;
    };

    const syncPrograms = async () => {
        const response = await $http.post(`${baseUrl()}/sync/programs`, {}, { errorHandle: false });
        return response.data;
    };

    // One program (after-save trigger); the server also refreshes the
    // offered-programs sets of the facilities the program touches.
    const syncProgram = async (healthcareServiceId) => {
        const response = await $http.post(`${baseUrl()}/sync/program/${healthcareServiceId}`, {}, { errorHandle: false });
        return response.data;
    };

    // Full (re)build of OT facilities (with offered programs) from Fresh.
    const syncFacilities = async () => {
        const response = await $http.post(`${baseUrl()}/sync/facilities`, {}, { errorHandle: false });
        return response.data;
    };

    // One location (after-save trigger); recomputes its offered programs.
    const syncFacility = async (locationId) => {
        const response = await $http.post(`${baseUrl()}/sync/facility/${locationId}`, {}, { errorHandle: false });
        return response.data;
    };

    // Re-sync a user's OT record from Fresh — demographics (name, login
    // email), active/inactive status, and facility access derived from their
    // location assignments (after user-management saves).
    const syncUser = async (practitionerId) => {
        const response = await $http.post(`${baseUrl()}/sync/user/${practitionerId}`, {}, { errorHandle: false });
        return response.data;
    };

    // Link a patient to OutcomeTools: creates the OT client keyed by
    // sorId = Patient.id and mirrors the OT clientId back onto
    // Patient.outcomeToolsId. A duplicate in OT resolves as already-linked.
    const linkPatient = async (patientId) => {
        const response = await $http.post(`${baseUrl()}/patients/${patientId}/link`, {}, { errorHandle: false });
        return response.data;
    };

    // Settle a patient carrying OT_UNKNOWN_LINK: looks them up in OT by sorId
    // (their Fresh id, which for a Classic-migrated patient is the UUID OT already
    // knows them by) and either stores the OT clientId or clears the sentinel.
    // Returns { reconciled, clientId, linked }.
    const reconcilePatient = async (patientId) => {
        const response = await $http.post(`${baseUrl()}/patients/${patientId}/reconcile`, {}, { errorHandle: false });
        return response.data;
    };

    // One-time SSO deep link opening OT on this patient's page as the
    // signed-in Fresh user (their practitioner must be linked to an OT user).
    // Pass an instanceId to deep-link straight to a specific survey.
    const getPatientSso = async (patientId, page = 'client_summary', instanceId = null) => {
        const params = { page };
        if (instanceId) {
            params.instanceId = instanceId;
        }
        const response = await $http.get(`${baseUrl()}/patients/${patientId}/sso`, { params, errorHandle: false });
        return response.data;
    };

    // The OT instrument library (surveys that can be assigned). Each item
    // carries instrumentId, title (abbreviation), description (long name).
    const getInstruments = async () => {
        const response = await $http.get(`${baseUrl()}/instruments`, { errorHandle: false });
        return response.data?.instruments ?? [];
    };

    // This patient's OT related contacts (id + name) for the survey
    // "to be completed by" picker.
    const getSurveyRelated = async (patientId) => {
        const response = await $http.get(`${baseUrl()}/patients/${patientId}/related`, { errorHandle: false });
        return response.data?.related ?? [];
    };

    // This patient's OT program enrollments (code, label, status, admitDate)
    // for the survey "program" picker.
    const getSurveyPrograms = async (patientId) => {
        const response = await $http.get(`${baseUrl()}/patients/${patientId}/programs`, { errorHandle: false });
        return response.data?.programs ?? [];
    };

    // Assign (create) an ad-hoc survey. survey = { instrumentId, assignedTo,
    // assignedToType, method } (method: Email | Kiosk). Returns { success,
    // data } where data.instanceId identifies the created survey.
    const assignSurvey = async (patientId, survey) => {
        loading.value = true;
        error.value = null;
        try {
            const response = await $http.post(`${baseUrl()}/patients/${patientId}/surveys`, survey, { errorHandle: false });
            return response.data;
        } catch (err) {
            error.value = err;
            throw err;
        } finally {
            loading.value = false;
        }
    };

    // Staff-scoped SSO deep link that opens directly on a just-created survey
    // (the "complete now" path) — opens as the acting user's OT (staff) user.
    const getSurveyTakeLink = async (patientId, instanceId) => {
        return getPatientSso(patientId, 'client_surveys', instanceId);
    };

    // This patient's OT surveys within the server-computed dueDate window —
    // completed (for Latest results) plus active/expired (for Outstanding).
    // Each row carries instanceId, instrumentId, instrumentTitle, status,
    // scores.overall, completedAt, dueDate, expireDate, pdfUrl.
    const getPatientSurveys = async (patientId, params = {}) => {
        const response = await $http.get(`${baseUrl()}/patients/${patientId}/surveys`, { params, errorHandle: false });
        return response.data?.surveys ?? [];
    };

    // This patient's active OT alert bundles the clinician is a recipient of
    // ("assigned to you"), including ones they've acknowledged but not yet
    // cleared. Each item is a bundle (same shape as the workboard list).
    const getPatientAlerts = async (patientId) => {
        const response = await $http.get(`${baseUrl()}/patients/${patientId}/alerts`, { errorHandle: false });
        return response.data?.alerts ?? [];
    };

    // One completed survey's rendered PDF as a Blob. The server fetches it from
    // OT with the partner key, so this works for any VIEW_OUTCOMETOOLS user
    // regardless of whether they have their own OutcomeTools user.
    const getSurveyPdf = async (patientId, instanceId) => {
        const response = await $http.get(`${baseUrl()}/patients/${patientId}/surveys/${instanceId}/pdf`, { responseType: 'blob', errorHandle: false });
        return response.data;
    };

    // Full re-push of a linked patient's data set (demographics, episodes,
    // program enrollments with diagnoses, related contacts) — the
    // reconciliation fallback when an individual sync failed. Skips (no-op)
    // for unlinked patients.
    const syncPatient = async (patientId) => {
        const response = await $http.post(`${baseUrl()}/sync/patient/${patientId}`, {}, { errorHandle: false });
        return response.data;
    };

    // Granular after-save syncs for a linked patient. Each returns
    // { skipped: true } (no-op) when the patient is unlinked.
    const syncPatientDemographics = async (patientId) => {
        const response = await $http.post(`${baseUrl()}/sync/patient/${patientId}/demographics`, {}, { errorHandle: false });
        return response.data;
    };

    // Re-push one episode of care and all of its program enrollments.
    const syncPatientEpisode = async (patientId, episodeOfCareId) => {
        const response = await $http.post(`${baseUrl()}/sync/patient/${patientId}/episode/${episodeOfCareId}`, {}, { errorHandle: false });
        return response.data;
    };

    // Re-push one program enrollment (a discharge pushes the discharge date).
    const syncPatientEncounter = async (patientId, encounterId) => {
        const response = await $http.post(`${baseUrl()}/sync/patient/${patientId}/encounter/${encounterId}`, {}, { errorHandle: false });
        return response.data;
    };

    // A program encounter was deleted in Fresh — OT has no enrollment
    // delete, so this pauses the orphaned enrollment's surveys.
    const syncPatientEncounterDeleted = async (patientId, encounterId) => {
        const response = await $http.post(`${baseUrl()}/sync/patient/${patientId}/encounter/${encounterId}/deleted`, {}, { errorHandle: false });
        return response.data;
    };

    // Recompute the ordered diagnosis list and push it to every open enrollment.
    const syncPatientDiagnoses = async (patientId) => {
        const response = await $http.post(`${baseUrl()}/sync/patient/${patientId}/diagnoses`, {}, { errorHandle: false });
        return response.data;
    };

    // Re-push one related contact.
    const syncPatientRelated = async (patientId, relationshipId) => {
        const response = await $http.post(`${baseUrl()}/sync/patient/${patientId}/related/${relationshipId}`, {}, { errorHandle: false });
        return response.data;
    };

    //alerts

    // Open OT risk-alert bundles (one per survey submission) on patients in the
    // clinician's caseload, already scoped server-side to the ones they still
    // need to act on. `filters` may include status (default active), severity,
    // category. Returns { alerts, count } where each alert is a bundle.
    const listAlerts = async (filters = {}) => {
        const response = await $http.get(`${baseUrl()}/alerts`, { params: filters, errorHandle: false });
        return response.data;
    };

    // One alert bundle with its recipients and merged log. `alertId` may be a
    // bundle/instance id or an OT alert UID.
    const getAlert = async (alertId) => {
        const response = await $http.get(`${baseUrl()}/alerts/${alertId}`, { errorHandle: false });
        return response.data?.alert;
    };

    // Record that the clinician opened the alert (idempotent per user in OT);
    // cascades across the bundle. Returns the refreshed bundle.
    const markAlertViewed = async (alertId) => {
        const response = await $http.post(`${baseUrl()}/alerts/${alertId}/viewed`, {}, { errorHandle: false });
        return response.data?.alert;
    };

    // Acknowledge the alert bundle for the clinician. OT's acknowledge is a
    // note-less, per-recipient status flip, so no note is sent. Returns the
    // refreshed bundle.
    const acknowledgeAlert = async (alertId) => {
        const response = await $http.post(`${baseUrl()}/alerts/${alertId}/acknowledge`, {}, { errorHandle: false });
        return response.data?.alert;
    };

    // Clear the alert bundle with a required justification note. Unlike
    // acknowledge, this resolves the alert for the whole care team. Returns the
    // refreshed bundle.
    const clearAlert = async (alertId, justification) => {
        const response = await $http.post(`${baseUrl()}/alerts/${alertId}/clear`, { justification }, { errorHandle: false });
        return response.data?.alert;
    };

    // Fetch the triggering instrument's rendered PDF as a Blob. Goes through the
    // authenticated client (Bearer token + API host) — a bare window.open to the
    // URL can't carry the token and would hit the SPA origin instead.
    const fetchInstrumentPdf = async (alertId) => {
        const response = await $http.get(`${baseUrl()}/alerts/${alertId}/instrument-pdf`, { responseType: 'blob', errorHandle: false });
        return response.data;
    };

    return {
        loading,
        error,
        getStatus,
        enable,
        listUsers,
        addUser,
        linkUser,
        getStructureMapping,
        saveStructureMapping,
        syncPrograms,
        syncProgram,
        syncFacilities,
        syncFacility,
        syncUser,
        linkPatient,
        reconcilePatient,
        getPatientSso,
        getInstruments,
        getSurveyRelated,
        getSurveyPrograms,
        assignSurvey,
        getSurveyTakeLink,
        getPatientSurveys,
        getPatientAlerts,
        getSurveyPdf,
        syncPatient,
        syncPatientDemographics,
        syncPatientEpisode,
        syncPatientEncounter,
        syncPatientEncounterDeleted,
        syncPatientDiagnoses,
        syncPatientRelated,
        listAlerts,
        getAlert,
        markAlertViewed,
        acknowledgeAlert,
        clearAlert,
        fetchInstrumentPdf,
    };
}
